import {Component, inject} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MatMiniFabButton} from "@angular/material/button";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {BaseDesignBlockDialogData, DesignBlockData, DesignBlockFormField} from "./types/base-design-block.types";
import {designBlockFormFields} from "./configs/design-block-form-fields";
import {defaultDesignBlockData} from "./configs/default-design-block-data";
import {BaseDesignBlockPathStateService} from "./services/base-design-block-path-state.service";
import {BaseDesignBlockModalService} from "./services/base-design-block-modal.service";
import {MatIconModule} from "@angular/material/icon";
import {titleModal} from "./configs/base-design-block-modal-config";
import {BaseDesignBlockFormComponent} from "./components/base-design-block-form/base-design-block-form.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-base-design-block-modal',
  imports: [
    BaseModalComponent,
    MatMiniFabButton,
    MatIconModule,
    BaseDesignBlockFormComponent,
    LoaderComponent
  ],
  templateUrl: './base-design-block-modal.component.html',
  styleUrl: './base-design-block-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class BaseDesignBlockModalComponent extends BaseModalComponent {
  public baseDesignBlockDialogData = inject<BaseDesignBlockDialogData>(MAT_DIALOG_DATA);
  private baseDesignBlockModalService: BaseDesignBlockModalService = inject(BaseDesignBlockModalService);
  private baseDesignBlockPathStateService: BaseDesignBlockPathStateService = inject(BaseDesignBlockPathStateService);

  public designBlocksData: Array<DesignBlockData> = [];
  public designBlockFormGroups: Array<FormGroup> = [];
  public noFirstData: boolean = false;
  public isValid: boolean = false;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.baseDesignBlockDialogData.modalKey);
    this.baseDesignBlockPathStateService.resetPathState();
  }

  public getTitleModal(): string {
    return titleModal[this.baseDesignBlockDialogData.modalKey];
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<DesignBlockFormField>(designBlockFormFields);
    this.designBlockFormGroups.push(formGroup);
    this.designBlocksData.push(defaultDesignBlockData);
    this.isValid = false;
  }

  public updateDesignBlockData(designBlockData: DesignBlockData, index: number): void {
    this.designBlocksData[index] = designBlockData;
    this.designBlockFormGroups.at(index)?.patchValue(designBlockData);
    this.isValid = !!designBlockData.id;
    this.noFirstData = !!this.designBlocksData.length;
  }

  public deleteDesignBlockData(index: number): void {
    this.designBlockFormGroups.splice(index, 1);
    this.designBlocksData.splice(index, 1);
    this.isValid = !!this.designBlocksData.length;
    this.noFirstData = !!this.designBlocksData.length;
    !this.isValid && this.handleAddNew();
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.baseDesignBlockDialogData.modalKey);
    this.loadingService.setIsLoadingState(true);
    this.baseDesignBlockPathStateService.setPathState(this.baseDesignBlockDialogData.modalKey);
    await this.fetchDesignBlocksData();
    this.isValid = !!this.designBlocksData.at(0)?.id;
    this.noFirstData = !!this.designBlocksData.at(0)?.id;
  }

  private async fetchDesignBlocksData(): Promise<void> {
    const params = this.baseDesignBlockModalService.setDataInModalParams(this.baseDesignBlockDialogData.parentId);
    this.designBlocksData = await this.baseDesignBlockModalService.getDataInModal<DesignBlockData>(params)
      .catch(() => [])
      .finally(() => this.loadingService.setIsLoadingState(false));
    this.initForm();
  }

  private initForm(): void {
    if (!this.designBlocksData.length) {
      this.handleAddNew();
      return;
    }

    this.designBlockFormGroups = this.designBlocksData.map(data => {
      const formGroup = this.baseModalService.getFormGroup<DesignBlockFormField>(designBlockFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });
  }
}
